import { generateStaticUrl } from "shared/imports/sharedHelperFunctions"
import { worldRegionsMapper } from "./world-regions-mapper"

export const worldMapConfig = {
    geoDataUrl: generateStaticUrl("data/maps/world-atlas.json"),
    useResourceId: false,
    getLabelOffsets: (width) => ({
        [DjangIO.app.models.Region.california.value]: [width, 30],
    }),
    outsideLabels: [],
    excludeLabels: [],
    formatGeoData: (data) => {
        const getCountryEnumFromName = (name) =>
            DjangIO.app.person.types.Country.items().find((item) => item.label === name)

        // eslint-disable-next-line no-undef
        const countries = topojson
            .feature(data, data.objects.countries)
            .features.map((region) => {
                const regionName = worldRegionsMapper.has(region.properties.name)
                    ? worldRegionsMapper.get(region.properties.name)
                    : region.properties.name
                const countryEnum = getCountryEnumFromName(regionName)

                if (!countryEnum) {
                    return null
                }

                return {
                    ...region,
                    enumId: countryEnum.value,
                    resourceId: countryEnum.database_id,
                    long_display: countryEnum.label,
                }
            })
            .filter(Boolean)

        return countries
    },
    useProportionalOffset: true,
    hideLabels: true,
    getMapProjection: (width, height) =>
        d3.geo
            .mercator()
            .scale(90)
            .translate([width / 2, height / 2])
            .center([0, 45]),
}

export const federalMapConfig = {
    geoDataUrl: generateStaticUrl("data/maps/us.static"),
    useResourceId: true,
    getLabelOffsets: (width, height) => ({
        [DjangIO.app.models.Region.california.value]: [width, 30],
        [DjangIO.app.models.Region.connecticut.value]: [30, 30],
        [DjangIO.app.models.Region.delaware.value]: [30, 50],
        [DjangIO.app.models.Region.district_of_columbia.value]: [11, 50],
        [DjangIO.app.models.Region.florida.value]: [80, 100],
        [DjangIO.app.models.Region.hawaii.value]: [45, -height],
        [DjangIO.app.models.Region.kentucky.value]: [100, height],
        [DjangIO.app.models.Region.louisiana.value]: [-100, -height],
        [DjangIO.app.models.Region.maryland.value]: [20, 20],
        [DjangIO.app.models.Region.massachusetts.value]: [25, -100],
        [DjangIO.app.models.Region.michigan.value]: [80, 17],
        [DjangIO.app.models.Region.new_hampshire.value]: [20, 2500],
        [DjangIO.app.models.Region.new_jersey.value]: [30, 100],
        [DjangIO.app.models.Region.new_york.value]: [120, height],
        [DjangIO.app.models.Region.rhode_island.value]: [25, 100],
        [DjangIO.app.models.Region.tennessee.value]: [width, 90],
        [DjangIO.app.models.Region.vermont.value]: [-100, -20],
    }),
    outsideLabels: [
        DjangIO.app.models.Region.connecticut.value,
        DjangIO.app.models.Region.delaware.value,
        DjangIO.app.models.Region.district_of_columbia.value,
        DjangIO.app.models.Region.hawaii.value,
        DjangIO.app.models.Region.maryland.value,
        DjangIO.app.models.Region.massachusetts.value,
        DjangIO.app.models.Region.new_hampshire.value,
        DjangIO.app.models.Region.new_jersey.value,
        DjangIO.app.models.Region.rhode_island.value,
        DjangIO.app.models.Region.vermont.value,
    ],
    excludeLabels: [DjangIO.app.models.Region.district_of_columbia.value],
    formatGeoData: (data) => {
        // we're indexing everything in the map based on state Region enums!
        const getRegionEnumFromFips = (fips) =>
            DjangIO.app.models.Region.items()
                .filter((item) => item.is_state_region)
                .find((item) => item.fips_code === fips)

        // topojson is attached to the window in frontend/index.js, we'll just
        // accept that.
        // eslint-disable-next-line no-undef
        const formattedStateData = topojson.feature(data, data.objects.states).features.map((region) => {
            const regionEnum = getRegionEnumFromFips(region.id)
            return {
                ...region,
                enumId: regionEnum.value,
                resourceId: regionEnum.database_id,
                short_display: regionEnum.abbrev.toUpperCase(),
                long_display: regionEnum.region_name,
            }
        })

        return formattedStateData
    },
    getMapProjection: (width, height) => d3.geo.albersUsa().scale(2 * height),
    useProportionalOffset: true,
}

export const euMapConfig = {
    geoDataUrl: generateStaticUrl("data/maps/euro_geo.json"),
    useResourceId: true,
    useProportionalOffset: false,
    getLabelOffsets: () => ({
        [DjangIO.app.person.types.Country.Sweden.value]: [-3, 0],
        [DjangIO.app.person.types.Country.Ireland.value]: [1, 3],
        [DjangIO.app.person.types.Country.Hungary.value]: [0, 5],
        [DjangIO.app.person.types.Country.Bulgaria.value]: [0, 4],
        [DjangIO.app.person.types.Country.Slovakia.value]: [28, 0],
        [DjangIO.app.person.types.Country.Finland.value]: [7, 2],
        [DjangIO.app.person.types.Country.Estonia.value]: [2, 2],
        [DjangIO.app.person.types.Country.Latvia.value]: [7, 3],
        [DjangIO.app.person.types.Country.CzechRepublic.value]: [-3, 3],
        [DjangIO.app.person.types.Country.UnitedKingdom.value]: [6, 21],
        [DjangIO.app.person.types.Country.Austria.value]: [4, 3],
        [DjangIO.app.person.types.Country.Greece.value]: [10, 1],
        [DjangIO.app.person.types.Country.Portugal.value]: [-12, 0],
        [DjangIO.app.person.types.Country.Belgium.value]: [-8, -20],
        [DjangIO.app.person.types.Country.Luxembourg.value]: [13, 3],
        [DjangIO.app.person.types.Country.Italy.value]: [-3, 20],
        [DjangIO.app.person.types.Country.Croatia.value]: [10, 10],
        [DjangIO.app.person.types.Country.Slovenia.value]: [-7, 17],
        [DjangIO.app.person.types.Country.Denmark.value]: [-16, -10],
        [DjangIO.app.person.types.Country.Netherlands.value]: [3, -15],
        [DjangIO.app.person.types.Country.Lithuania.value]: [3, 5],
    }),
    outsideLabels: [
        DjangIO.app.person.types.Country.Portugal.value,
        DjangIO.app.person.types.Country.Greece.value,
        DjangIO.app.person.types.Country.Belgium.value,
        DjangIO.app.person.types.Country.Denmark.value,
        DjangIO.app.person.types.Country.Italy.value,
        DjangIO.app.person.types.Country.Netherlands.value,
        DjangIO.app.person.types.Country.Croatia.value,
        DjangIO.app.person.types.Country.Slovenia.value,
        DjangIO.app.person.types.Country.Slovakia.value,
    ],
    excludeLabels: [],
    formatGeoData: (data) => {
        const getCountryEnumFromName = (name) =>
            DjangIO.app.person.types.Country.items()
                .filter((country) => country.is_eu)
                .find((item) => item.label === name)

        // topojson is attached to the window in frontend/index.js, we'll just
        // accept that.
        const formattedCountryData = data.features
            .filter((region) => getCountryEnumFromName(region.properties.name))
            .map((region) => {
                const countryEnum = getCountryEnumFromName(region.properties.name)
                return {
                    ...region,
                    enumId: countryEnum.value,
                    resourceId: countryEnum.database_id,
                    short_display: countryEnum.abbrev.toUpperCase(),
                    long_display: countryEnum.label,
                }
            })

        return formattedCountryData
    },
    getMapProjection: (width, height) =>
        d3.geo
            .mercator()
            .center([10, 57])
            .scale(height * 0.93),
}
